import { nextDelayMs, RETRY_POLICY } from "./tradeSync.constants";

export interface ConvergenceRun {
  /** Resolves when the run ends, whatever the outcome. */
  done: Promise<ConvergenceOutcome>;
  /** Stop early — unmount, account change, or a newer run superseding this one. */
  cancel: () => void;
}

export type ConvergenceOutcome =
  | { status: "converged"; attempts: number }
  | { status: "exhausted"; attempts: number }
  | { status: "errored"; attempts: number }
  | { status: "cancelled"; attempts: number };

export interface RunUntilConvergedOptions<T> {
  /** One network read. Must resolve with the RAW server answer. */
  refetch: () => Promise<T>;
  /** Did that answer prove the server caught up? */
  isConverged: (result: T) => boolean;
  onAttempt?: (attempt: number) => void;
  /** Injected for deterministic tests. */
  now?: () => number;
  random?: () => number;
  setTimeoutFn?: (handler: () => void, ms: number) => number;
  clearTimeoutFn?: (handle: number) => void;
}

/**
 * Refetch until the server proves it caught up, then STOP.
 *
 * This replaces a fixed ladder that fired every rung unconditionally, whether
 * the data arrived on the first read or never. The schedule here bounds only
 * how often we ask and for how long; termination is decided by observed
 * evidence, so a backend that answers immediately costs exactly one request.
 *
 * Each delay is measured from when the previous attempt SETTLED, so slow
 * responses cannot pile requests up on top of each other.
 */
export function runUntilConverged<T>({
  refetch,
  isConverged,
  onAttempt,
  now = Date.now,
  random = Math.random,
  setTimeoutFn = (handler, ms) => window.setTimeout(handler, ms),
  clearTimeoutFn = (handle) => window.clearTimeout(handle),
}: RunUntilConvergedOptions<T>): ConvergenceRun {
  const startedAt = now();
  let cancelled = false;
  let timer: number | undefined;
  let attempts = 0;

  // Resolver for the backoff currently being awaited, so `cancel()` can wake the
  // loop instead of leaving it suspended forever.
  let wakeFromWait: (() => void) | undefined;

  const cancel = () => {
    cancelled = true;
    if (timer !== undefined) clearTimeoutFn(timer);
    // Without this the loop stays parked on `await wait(...)` and `done` never
    // settles — leaking one suspended run per superseded trade and per unmount.
    wakeFromWait?.();
    wakeFromWait = undefined;
  };

  const wait = (ms: number) =>
    new Promise<void>((resolve) => {
      wakeFromWait = resolve;
      timer = setTimeoutFn(() => {
        wakeFromWait = undefined;
        resolve();
      }, ms);
    });

  const done = (async (): Promise<ConvergenceOutcome> => {
    let consecutiveErrors = 0;

    for (let attempt = 0; attempt < RETRY_POLICY.maxAttempts; attempt += 1) {
      if (cancelled) return { status: "cancelled", attempts };

      const delay = nextDelayMs(attempt, random);
      if (delay > 0) {
        // eslint-disable-next-line no-await-in-loop
        await wait(delay);
        if (cancelled) return { status: "cancelled", attempts };
      }

      // The wall clock is checked before spending a request, not after, so a
      // run cannot exceed its budget by the length of one slow fetch.
      if (now() - startedAt >= RETRY_POLICY.maxWallClockMs) {
        return { status: "exhausted", attempts };
      }

      attempts += 1;
      onAttempt?.(attempts);

      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await refetch();
        consecutiveErrors = 0;
        if (cancelled) return { status: "cancelled", attempts };
        if (isConverged(result)) return { status: "converged", attempts };
      } catch {
        consecutiveErrors += 1;
        // Repeated failures mean a truthful "we don't know" beats spending the
        // rest of the budget on an endpoint that is down.
        if (consecutiveErrors >= RETRY_POLICY.maxConsecutiveErrors) {
          return { status: "errored", attempts };
        }
      }
    }

    return { status: "exhausted", attempts };
  })();

  return { done, cancel };
}
